import React, { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Button, Dialog, Tabs } from '@node-space/ui-components'
import { OnceOffExportContextProvider } from 'contexts/onceOffExportContext'
import { RecurringExportContextProvider } from 'contexts/recurringExportContext'
import { useReportScheduleQuery } from 'hooks/queries/useReportScheduleQuery'
import { useReportScheduleTimezonesQuery } from 'hooks/queries/useReportScheduleTimezonesQuery'
import { CustomerWallet } from 'types/customers'
import {
  ExportTabItems,
  ExportTabItemsType,
  RecurringExportForm,
  RecurringExportSteps,
} from 'types/walletTransactionsExport'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { useOnceOffExportHelper } from './hooks/useOnceOffExportHelper'
import { useRecurringExportHelper } from './hooks/useRecurringExportHelper'
import { OnceOffExport } from './OnceOffExport/OnceOffExport'
import { RecurringExport } from './RecurringExport/RecurringExport'

interface TransactionExportModalProps {
  handleClose: () => void
  downloadAll: boolean
  customerWallet?: CustomerWallet
}

export const TransactionExportModal = ({
  handleClose,
  downloadAll,
  customerWallet,
}: TransactionExportModalProps) => {
  const { t } = useTranslation()
  const [openTab, setOpenTab] = useState<ExportTabItemsType>(ExportTabItems.ONCE_OFF)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.EXPORT_TRANSACTION_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.LOAD,
    })
  }, [])

  const onceOffValues = useOnceOffExportHelper({ downloadAll, handleClose, customerWallet })
  const recurringValues = useRecurringExportHelper({
    handleClose,
    setOpenTab,
  })

  const { control } = recurringValues.form

  const recurringformValues = useWatch({ control }) as RecurringExportForm

  const { enableRecurringExports } = useFeatureFlags()

  const { isFetching: isFetchingTimezones } = useReportScheduleTimezonesQuery(
    openTab === ExportTabItems.RECURRING && recurringValues?.step === RecurringExportSteps.FORM
  )

  const {
    data: reportSchedules = [],
    isFetching,
    isError,
  } = useReportScheduleQuery(openTab === ExportTabItems.RECURRING)

  const isLoadingCreateSchedule =
    isFetchingTimezones || recurringValues?.isLoadingPostReportSchedule
  const isLoadingUpdateSchedule = isFetchingTimezones || recurringValues?.isLoadingPutReportSchedule

  const CancelButton = (
    <Button key="cancel" onClick={handleClose} variant="secondary">
      {t('cancel')}
    </Button>
  )

  const getPrimaryAction = () => {
    if (openTab === ExportTabItems.ONCE_OFF || !(enableRecurringExports && downloadAll)) {
      return (
        <Button
          key="exportToEmail"
          onClick={onceOffValues?.handleTransactionExportToEmail}
          loading={onceOffValues?.isExporting}
          disabled={
            (onceOffValues?.formValues?.selectedDateOptionName ==
              onceOffValues?.exportCustomDateOptions?.customDateRange?.name &&
              !onceOffValues?.dateValue?.from &&
              !onceOffValues?.dateValue?.to) ||
            (onceOffValues?.formValues?.reportType === 'BALANCE' &&
              !onceOffValues?.formValues?.balanceDate)
          }
        >
          {t('walletsPage.exportToEmail')}
        </Button>
      )
    }
    if (openTab === ExportTabItems.RECURRING && !isFetching && !isError) {
      return recurringValues?.isUpdatingReportSchedule ? (
        <Button
          key="updateSchedule"
          onClick={() => recurringValues?.updateReportSchedule(recurringformValues)}
          disabled={isLoadingUpdateSchedule}
          loading={isLoadingUpdateSchedule}
        >
          {t('exports.updateSchedule')}
        </Button>
      ) : (
        <Button
          key="createSchedule"
          onClick={() => recurringValues?.createReportSchedule(recurringformValues)}
          disabled={isLoadingCreateSchedule || !!reportSchedules?.length}
          loading={isLoadingCreateSchedule}
        >
          {t('exports.createSchedule')}
        </Button>
      )
    }
  }

  const handleTabChange = (name: string) => {
    setOpenTab(name as ExportTabItemsType)
    recurringValues?.setStep(RecurringExportSteps.LIST)
    recurringValues?.setCurrentReportSchedule(null)
    recurringValues?.setIsUpdatingReportSchedule(false)
  }

  return (
    <Dialog
      title={t('walletsPage.exportTransactionNavBarTitle')}
      type="utility"
      open
      onClose={handleClose}
      ActionButtons={[getPrimaryAction(), CancelButton]}
    >
      {enableRecurringExports && downloadAll ? (
        <Tabs
          defaultTabId={ExportTabItems.ONCE_OFF}
          onChange={handleTabChange}
          tabs={[
            {
              header: t('exports.onceOff'),
              id: ExportTabItems.ONCE_OFF,
              testId: ExportTabItems.ONCE_OFF,
              content: (
                <OnceOffExportContextProvider value={onceOffValues}>
                  <OnceOffExport downloadAll={downloadAll} />
                </OnceOffExportContextProvider>
              ),
            },
            {
              header: t('exports.recurring'),
              id: ExportTabItems.RECURRING,
              testId: ExportTabItems.RECURRING,
              content: (
                <RecurringExportContextProvider value={recurringValues}>
                  <RecurringExport downloadAll={downloadAll} />
                </RecurringExportContextProvider>
              ),
            },
          ]}
        />
      ) : (
        <OnceOffExportContextProvider value={onceOffValues}>
          <OnceOffExport downloadAll={downloadAll} />
        </OnceOffExportContextProvider>
      )}
    </Dialog>
  )
}
